import { 
  Bars3Icon as Menu, 
  MagnifyingGlassIcon as Search, 
  BellIcon as Bell, 
  Cog6ToothIcon as Settings
} from '@heroicons/react/24/outline';
import { useUI } from '../../stores/appStore';
import { useTheme } from '../providers/ThemeProvider';
import { WebSocketStatus } from '../common/WebSocketStatus';
import { UserMenu } from '../auth/UserMenu';

export function Header() {
  const { toggleSidebar, notifications } = useUI();
  const { theme, setTheme } = useTheme();

  const unreadCount = notifications.filter(n => !n.read).length;

  const themeOptions = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ] as const;

  return (
    <header className="relative z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 shadow" role="banner">
      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="px-3 sm:px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
        aria-label="Toggle navigation menu"
        aria-expanded={false}
      >
        <Menu className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
      </button>

      {/* Search bar and actions */}
      <div className="flex-1 px-3 sm:px-4 flex justify-between items-center">
        <div className="flex-1 max-w-lg">
          {/* Mobile search icon */}
          <button 
            className="sm:hidden p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
            aria-label="Open search"
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </button>
          
          {/* Desktop search bar */}
          <div className="relative hidden sm:block" role="search">
            <label htmlFor="header-search" className="sr-only">Search apps and connections</label>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              id="header-search"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Search apps, connections..."
              type="search"
              aria-label="Search apps and connections"
            />
          </div>
        </div>

        {/* Right side actions */}
        <div className="ml-2 sm:ml-4 flex items-center space-x-1 sm:space-x-2">
          {/* WebSocket Status - hidden on mobile */}
          <div className="hidden sm:block">
            <WebSocketStatus />
          </div>
          
          {/* Theme selector - hidden on small mobile */}
          <div className="relative hidden xs:block">
            <label htmlFor="theme-selector" className="sr-only">Choose theme</label>
            <select
              id="theme-selector"
              value={theme}
              onChange={(e) => setTheme(e.target.value as any)}
              className="appearance-none bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Choose theme"
            >
              {themeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notifications */}
          <button 
            className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md relative"
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 h-3 w-3 sm:h-4 sm:w-4 bg-red-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center"
                aria-hidden="true"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Settings - hidden on mobile */}
          <button 
            className="hidden sm:block p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* User menu */}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}